import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import config from "../config";
import i18n from "../i18n";
import { formatDateTimeGR } from "./shared/dateUtils";
import useStyleSheet from "../useStyleSheet";
import "./BuzzerFeedback.css";
import BrandMark from "./BrandMark";

const tr = i18n.t.bind(i18n);

/** Premium metallic icon: inherits currentColor */
const BuzzerIcon = ({ size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3a2 2 0 0 1 2 2v1.1A7 7 0 0 1 19 13v2H5v-2a7 7 0 0 1 5-6.9V5a2 2 0 0 1 2-2z" />
    <path d="M4 16h16a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z" />
    <circle cx="12" cy="5" r="1" />
  </svg>
);

const WaveIcon = ({ side = "left", active = false, size = 22 }) => (
  <span className={`luxWaveWrap ${side === "left" ? "isLeft" : "isRight"} ${active ? "isActive" : ""}`}>
    <svg
      className="luxWaveSvg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M8 8c2.5 2.5 2.5 5.5 0 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 6c4 4 4 8 0 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);

const CalendarIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none">
    <path d="M7 3v3M17 3v3M4 9h16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    <rect x="4" y="5" width="16" height="15" rx="3.2" stroke="currentColor" strokeWidth="1.9" />
  </svg>
);

const ClockIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.9" />
    <path d="M12 8.2v4.3l3 1.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TableIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="none">
    <rect x="5" y="7" width="14" height="8" rx="2.2" stroke="currentColor" strokeWidth="1.9" />
    <path d="M8 15v3M16 15v3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

function MarketingBottomBar() {
  return (
    <footer className="luxFooter">
      <a href="/home" className="luxFooterLink" aria-label="Go to EstiaSync home">
        <span className="luxFooterText">
          <span className="luxFooterPowered">Powered by:</span>
          <span className="luxFooterBrandWrap">
            <BrandMark 
              href=""
              size={28}
              withIcon={true}
              withText={true}
              variant="default"
              className="luxFooterBrandMark"
            />
          </span>
        </span>

        {/* decorative metallic sweeps */}
        <span className="luxFooterSheen" aria-hidden="true" />
        <span className="luxFooterGlow" aria-hidden="true" />
      </a>
    </footer>
  );
}


const SyncProgressRing = ({
  size = 60,
  progress = 0,
  label = "",
  className = "",
  variant = "normal"
}) => {
  const sw = variant === "bezel" ? 5 : 6;
  const r  = variant === "bezel" ? (32 - sw/2 - 0.6) : 22;

  const c = 2 * Math.PI * r;
  const dash = c * (1 - Math.min(1, Math.max(0, progress)));

  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" className={`luxRing ${className}`}>
      <defs>
        <linearGradient id="syncGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0ea5e9" />
          <stop offset="45%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#10b981" />
        </linearGradient>
      </defs>

      {/* track */}
      <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(15,23,42,0.20)" strokeWidth={sw} />

      {/* progress */}
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke="url(#syncGrad)"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={dash}
        transform="rotate(-90 32 32)"
      />

      {!!label && (
        <text
          x="32"
          y="36"
          textAnchor="middle"
          fontSize="14"
          fontWeight="900"
          fill="rgba(15,23,42,0.92)"
          style={{ paintOrder: "stroke", stroke: "rgba(255,255,255,0.55)", strokeWidth: 0.8 }}
        >
          {label}
        </text>
      )}
    </svg>
  );
};

const StarRow = ({ value, onChange, label }) => (
  <div className="luxQRow">
    <div className="luxQLabel">{label}</div>
    <div className="luxStars" role="radiogroup" aria-label={label}>
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          type="button"
          className={`luxStar ${n <= value ? "isOn" : ""}`}
          onClick={() => onChange(n)}
          aria-label={`${label} ${n}/5`}
        >
          ★
        </button>
      ))}
    </div>
  </div>
);

export default function BuzzerFeedback() {
  const { id, sig } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const cssReady = useStyleSheet("/marketing.css");

  const COOLDOWN_MS = 30_000;
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());
  const [isRinging, setIsRinging] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [showCooldownUI, setShowCooldownUI] = useState(false);
  const [showTimerFace, setShowTimerFace] = useState(false);
  const [preFlipBurst, setPreFlipBurst] = useState(false);

  const remainingMs = Math.max(0, cooldownUntil - nowMs);
  const isCooldown = remainingMs > 0;
  const remainingSec = Math.ceil(remainingMs / 1000);
  const progress = isCooldown ? remainingMs / COOLDOWN_MS : 0;

  const [fbOrg, setFbOrg] = useState(0);
  const [fbQCost, setFbQCost] = useState(0);
  const [fbEnv, setFbEnv] = useState(0);
  const [fbText, setFbText] = useState("");
  const [fbSending, setFbSending] = useState(false);
  const [fbSent, setFbSent] = useState(false);
  const [fbErr, setFbErr] = useState("");
  const [uiPhase, setUiPhase] = useState("buzzer"); 
  
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [msgErr, setMsgErr] = useState("");

  const [identityOpen, setIdentityOpen] = useState(false);
  const [identityDismissed, setIdentityDismissed] = useState(false);
  const [identityPhone, setIdentityPhone] = useState("");
  const [identityLastname, setIdentityLastname] = useState("");
  const [identityFirstname, setIdentityFirstname] = useState("");
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identitySaved, setIdentitySaved] = useState(false);
  const [identityErr, setIdentityErr] = useState("");

  const [marketingEmail, setMarketingEmail] = useState("");
  const [marketingSending, setMarketingSending] = useState(false);
  const [marketingSaved, setMarketingSaved] = useState(false);
  const [marketingClosing, setMarketingClosing] = useState(false);
  const [marketingHidden, setMarketingHidden] = useState(false);
  const [marketingErr, setMarketingErr] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);
  
  const targetSurveyMode = data?.mode === "feedback" || data?.mode === "thanks" || fbSent;

  const fetchPublicPayload = async (signal) => {
    const url = tr("{{API_BASE_URL}}/public/buzzer/{{id}}/{{sig}}", {
      API_BASE_URL: config.API_BASE_URL,
      id,
      sig
    });
  
    const resp = await fetch(url, { credentials: "omit", signal });
  
    let payload = null;
    try { payload = await resp.json(); } catch {}
  
    if (!resp.ok) {
      const msg = payload?.error || "Not available";
      throw new Error(String(msg));
    }
  
    return payload;
  };

  const rootRef = useRef(null);

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
  
    (async () => {
      try {
        setErr("");
        const payload = await fetchPublicPayload(ac.signal);
        if (!alive) return;
        setData(payload);
      } catch (e) {
        if (!alive) return;
        if (e?.name === "AbortError") return;
        setErr(String(e?.message || "Not available"));
      }
    })();
  
    return () => {
      alive = false;
      ac.abort();
    };
  }, [id, sig]);

  useEffect(() => {
    if (isCooldown) return;            // don’t refresh while countdown runs
    if (!cssReady) return;
    if (!id || !sig) return;
    if (data?.mode === "thanks" || fbSent) return;
  
    const t = setInterval(async () => {
      try {
        const payload = await fetchPublicPayload(undefined);
        setData(payload);
      } catch {
        // ignore: keep UX stable
      }
    }, 5_000);
  
    return () => clearInterval(t);
  }, [isCooldown, cssReady, id, sig, data?.mode, fbSent]);


  useEffect(() => {
    if (!data) return;

    if (data.client_email) {
      setMarketingEmail((prev) => prev || data.client_email || "");
    }

    if (data.needsIdentity && !identityDismissed) {
      setIdentityPhone((prev) => prev || data.client_phone || "");
      setIdentityOpen(true);
    }

    if (!data.needsIdentity) {
      setIdentityOpen(false);
    }
  }, [data, identityDismissed]);


  useEffect(() => {
    if (!targetSurveyMode) {
      setUiPhase("buzzer");
      return;
    }
  
    // already in feedback
    if (uiPhase === "feedback") return;
  
    // start transition
    setUiPhase("toFeedback");
  
    const t = setTimeout(() => {
      setUiPhase("feedback");
    }, 520); // match CSS timing
  
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSurveyMode]);


  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!isCooldown) {
      setShowCooldownUI(false);
      setShowTimerFace(false); // flip back to buzzer
    }
  }, [isCooldown]);

  useEffect(() => {
    if (data?.mode === "thanks") setFbSent(true);
  }, [data?.mode]);

  useEffect(() => {
    // when we land in thanks, kill keyboard + reset any visual viewport offset
    if (!(fbSent || data?.mode === "thanks")) return;
  
    // close keyboard
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
  
    // reset scroll (both window + internal root if any)
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      rootRef.current?.scrollTo?.(0, 0);
    });
  }, [fbSent, data?.mode]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lang = (params.get("lang") || "").toLowerCase();
    if (lang === "el" || lang === "en") {
      i18n.changeLanguage(lang);
    }
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
  
    const onResize = () => {
      // iOS: DO NOT use vv.offsetTop (causes stuck shifted UI after Done)
      const keyboardPx = Math.max(0, window.innerHeight - vv.height);
      const isKeyboard = keyboardPx > 40;
  
      document.documentElement.style.setProperty("--kb", isKeyboard ? `${keyboardPx}px` : "0px");
  
      if (isKeyboard) {
        document.body.classList.add("luxKeyboard");
      } else {
        document.body.classList.remove("luxKeyboard");
  
        // hard restore (no smooth) — fixes the "stuck shifted up" state
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
          rootRef.current?.scrollTo?.(0, 0);
        });
      }
    };
  
    onResize();
    vv.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
  
    return () => {
      vv.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      document.documentElement.style.removeProperty("--kb");
      document.body.classList.remove("luxKeyboard");
    };
  }, []);

  const submitFeedback = async () => {
    if (fbSending) return;
    setFbErr("");
  
    if (![fbOrg, fbQCost, fbEnv].every(v => v >= 1 && v <= 5)) {
      setFbErr(tr("Please rate all categories."));
      return;
    }
  
    setFbSending(true);
    try {
      const url = tr("{{API_BASE_URL}}/public/buzzer/{{id}}/{{sig}}/feedback", {
        API_BASE_URL: config.API_BASE_URL,
        id,
        sig
      });
  
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          org_service: fbOrg,
          quality_cost: fbQCost,
          environment_clean: fbEnv,
          comment: fbText
        })
      });
  
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || "Failed");
  
      setFbSent(true);
    } catch (e) {
      setFbErr(String(e?.message || "Failed"));
    } finally {
      setFbSending(false);
    }
  };

  const closeKeyboardAndRestoreView = () => {
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
  
    document.documentElement.style.setProperty("--kb", "0px");
    document.body.classList.remove("luxKeyboard");
  
    // hard reset scroll twice (iOS sometimes needs the second tick)
    window.scrollTo(0, 0);
    rootRef.current?.scrollTo?.(0, 0);
  
    setTimeout(() => {
      window.scrollTo(0, 0);
      rootRef.current?.scrollTo?.(0, 0);
      document.documentElement.style.setProperty("--kb", "0px");
      document.body.classList.remove("luxKeyboard");
    }, 50);
  };

  const RING_MS = 1200;
  const RING_DELAY_MS = 120;

  const triggerCallUX = () => {
    // IMPORTANT: keep buzzer face visible during the ring
    setShowTimerFace(false);
    setShowCooldownUI(false);
  
    setTimeout(() => {
      setIsRinging(true);
      setPreFlipBurst(true);
  
      if (navigator.vibrate) navigator.vibrate([80, 60, 80, 60, 120]);
  
      setTimeout(() => {
        setIsRinging(false);
        setPreFlipBurst(false);
  
        // AFTER ring ends -> flip to timer face
        setShowCooldownUI(true);
        setShowTimerFace(true);
      }, RING_MS);
    }, RING_DELAY_MS);
  };

  
  const handleBuzzerClick = () => {
    if (isCooldown || isRinging || isPressed) return;
  
    // press micro interaction
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 160);
  
    // start cooldown immediately (disable button)
    setCooldownUntil(Date.now() + COOLDOWN_MS);
    
    // ✅ create service action type=12 (public endpoint)
    (async () => {
      try {
        const url = tr("{{API_BASE_URL}}/public/buzzer/{{id}}/{{sig}}/action", {
          API_BASE_URL: config.API_BASE_URL,
          id,
          sig
        });
    
        await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit"
        });
      } catch {
        // ignore (UX must remain smooth)
      }
    })();
    triggerCallUX();
  };

  const sendBuzzerMessage = async () => {
    if (msgSending) return;
  
    const text = String(msgText || "").trim();
    if (!text) return;
    
    closeKeyboardAndRestoreView();
  
    setMsgErr("");
    setMsgSending(true);
    setMsgSent(false);
  
    // start cooldown immediately (same behavior as buzzer)
    setCooldownUntil(Date.now() + COOLDOWN_MS);
    triggerCallUX();
  
    try {
      const url = tr("{{API_BASE_URL}}/public/buzzer/{{id}}/{{sig}}/action", {
        API_BASE_URL: config.API_BASE_URL,
        id,
        sig
      });
  
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ message: text })
      });
  
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || "Failed");
  
      setMsgSent(true);
  
      // optional: clear text after a successful send
      setMsgText("");
      // reset the “sent” icon after a bit (but still disabled due to cooldown)
      setTimeout(() => setMsgSent(false), 1800);
    } catch (e) {
      setMsgErr(String(e?.message || "Failed"));
    } finally {
      setMsgSending(false);
      closeKeyboardAndRestoreView();
    }
  };


  const closeIdentityModal = () => {
    if (identitySaving || identitySaved) return;
    setIdentityOpen(false);
    setIdentityDismissed(true);
    setIdentityErr("");
    setIdentitySaved(false);
    closeKeyboardAndRestoreView();
  };

  const saveIdentity = async () => {
    if (identitySaving) return;

    const phone = String(identityPhone || "").trim();
    const lastname = String(identityLastname || "").trim();
    const firstname = String(identityFirstname || "").trim();

    if (!phone) {
      setIdentityErr(tr("Phone is required."));
      return;
    }

    setIdentityErr("");
    setIdentitySaving(true);

    try {
      const url = tr("{{API_BASE_URL}}/public/buzzer/{{id}}/{{sig}}/client", {
        API_BASE_URL: config.API_BASE_URL,
        id,
        sig
      });

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          phone,
          lastname,
          firstname
        })
      });

      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || "Failed");

      setData((prev) => ({
        ...(prev || {}),
        fullname: payload?.fullname || `${lastname} ${firstname}`.trim(),
        needsIdentity: false,
        client_phone: payload?.client?.phone || phone,
        client_email: payload?.client?.email || prev?.client_email || "",
        client_id: payload?.client?.id || prev?.client_id || null
      }));

      setIdentitySaved(true);
      setTimeout(() => {
        setIdentitySaved(false);
        setIdentityDismissed(true);
        setIdentityOpen(false);
        closeKeyboardAndRestoreView();
      }, 880);
    } catch (e) {
      setIdentityErr(String(e?.message || "Failed"));
    } finally {
      setIdentitySaving(false);
    }
  };

  const saveMarketingEmail = async () => {
    if (marketingSending || marketingSaved) return;

    const email = String(marketingEmail || "").trim().toLowerCase();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!emailOk) {
      setMarketingErr(tr("Please enter a valid email."));
      return;
    }

    setMarketingErr("");
    setMarketingSending(true);

    try {
      const url = tr("{{API_BASE_URL}}/public/buzzer/{{id}}/{{sig}}/email", {
        API_BASE_URL: config.API_BASE_URL,
        id,
        sig
      });

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          email,
          consent: true
        })
      });

      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(payload?.error || "Failed");

      setMarketingSaved(true);
      setData((prev) => ({
        ...(prev || {}),
        client_email: payload?.client?.email || email,
        client_id: payload?.client?.id || prev?.client_id || null,
        needsIdentity: false
      }));

      setTimeout(() => setMarketingClosing(true), 520);
      setTimeout(() => setMarketingHidden(true), 980);
    } catch (e) {
      setMarketingErr(String(e?.message || "Failed"));
    } finally {
      setMarketingSending(false);
    }
  };

  const logo = data?.logoUrl || "/bbq_service_icon.ico";
  const tables = data?.tables || "";
  const arrival = data?.arrival ? formatDateTimeGR(data.arrival) : "";
  const fullname = data?.fullname || "";

  const companyLogoFile = data?.logo || "";
  const companyUrl = data?.url || "";

  const companyLogoSrc = companyLogoFile ? `/companies_logos/${companyLogoFile}` : logo;
  const companyLogoHref = companyUrl || "#";

  // “metal flicker seed” so it doesn’t sync across refreshes too predictably
  const shimmerSeed = useMemo(() => Math.floor(Math.random() * 9999), []);
  
  const isSurveyMode = uiPhase !== "buzzer";
  const isFeedbackReady = fbOrg >= 1 && fbQCost >= 1 && fbEnv >= 1;
  const phoneReady = !!String(identityPhone || "").trim();
  const emailReady = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(marketingEmail || "").trim());
  const isMarketingReady = emailReady;

  const arrivalDateObj = data?.arrival ? new Date(data.arrival) : null;
  const arrivalDateText = arrivalDateObj
    ? new Intl.DateTimeFormat(i18n.language === "el" ? "el-GR" : "en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      }).format(arrivalDateObj)
    : "";
  const arrivalTimeText = arrivalDateObj
    ? new Intl.DateTimeFormat(i18n.language === "el" ? "el-GR" : "en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).format(arrivalDateObj)
    : "";

  return (
    <div ref={rootRef}
      id="marketing-root"
      className={`luxRoot ${isRinging ? "isRinging" : ""} phase-${uiPhase}`}
      data-seed={shimmerSeed}
    >
      {/* ambient sparkles */}
      <div className="luxAmbient" aria-hidden="true" />
      <div className="luxVignette" aria-hidden="true" />

      <div className="luxWrap">
        {!cssReady ? (
          <div className="luxLoading">{tr("Loading...")}</div>
        ) : (
          <>
            {!err && !!data && !!companyLogoFile && (
              <a
                href={companyLogoHref}
                target={companyLogoHref && companyLogoHref !== "#" ? "_blank" : undefined}
                rel="noreferrer"
                className="luxBrandLink"
              >
                <div className="luxBrandFrame">
                  <img
                    src={companyLogoSrc}
                    alt={companyLogoFile || "company"}
                    className={`luxBrandImg ${isSurveyMode ? "isCompact" : ""}`}
                    onError={(e) => {
                      e.currentTarget.src = logo;
                    }}
                  />
                  <div className="luxBrandSheen" aria-hidden="true" />
                </div>
              </a>
            )}
            
            {uiPhase === "buzzer" && !err && !!data && !!tables && (
              <div className="luxTablesWrap">
                <span className="luxTablesChip">{tables}</span>
              </div>
            )}

            {(err || !data || uiPhase !== "buzzer" || data?.mode === "feedback" || data?.mode === "thanks" || fbSent) && (
              <div className="luxCardOuter">
                <div className="luxCard">
                  <div className="luxCardSheen" aria-hidden="true" />
                  <div className="luxCardEdge" aria-hidden="true" />
            
                  <div className="luxCardBody">
                    {err ? (
                      <div className="luxErr">{err}</div>
                    ) : !data ? (
                      <div className="luxSubtle">{tr("Loading...")}</div>
                    ) : (
                      <>
                      
                        {(data?.mode === "feedback" && !(fbSent || data?.mode === "thanks")) && (
                          <div className="luxSurvey">
                            <div className="luxSurveyTitle">
                              {tr("Rate your experience")}
                            </div>
                        
                            <StarRow
                              value={fbOrg}
                              onChange={setFbOrg}
                              label={tr("Managment & Service")}
                            />
                            
                            <StarRow
                              value={fbQCost}
                              onChange={setFbQCost}
                              label={tr("Quality / Cost")}
                            />
                            
                            <StarRow
                              value={fbEnv}
                              onChange={setFbEnv}
                              label={tr("Environment & Cleanliness")}
                            />
                        
                            <textarea
                              className="luxFeedbackBox"
                              value={fbText}
                              onChange={(e) => setFbText(e.target.value)}
                              placeholder={tr("Write a short comment (optional)...")}
                              maxLength={1500}
                            />
                        
                            {!!fbErr && <div className="luxFbErr">{fbErr}</div>}
                        
                            <button
                              className={`luxSendBtn ${fbSending ? "isSending" : ""} ${!isFeedbackReady ? "isDisabled" : ""}`}
                              onClick={submitFeedback}
                              disabled={fbSending || !isFeedbackReady}
                            >
                              {fbSending ? tr("Sending...") : tr("Send feedback")}
                            </button>
                          </div>
                        )}
                        
                        {(fbSent || data?.mode === "thanks") && (
                          <>
                            <div className="luxThanks">
                              <div className="luxThanksTitle">{tr("Thank you!")}</div>
                              <div className="luxThanksSub">{tr("Your feedback has been submitted.")}</div>
                            </div>

                            {!marketingHidden && (
                              <div className={`luxConsentCard ${marketingClosing ? "isClosing" : ""} ${marketingSaved ? "isSaved" : ""}`}>
                                <div className="luxConsentTitle">{tr("Leave your email")}</div>
                                <div className="luxConsentSub">
                                  {tr("Receive news and offers from the restaurant. By sending your email address, you accept the ")}
                                  <button
                                    type="button"
                                    className="luxTermsLink"
                                    onClick={() => setTermsOpen(true)}
                                  >
                                    {tr("terms & conditions")}
                                  </button>
                                  .
                                </div>

                                <div className="luxConsentRow">
                                  <input
                                    className="luxEmailInput"
                                    type="email"
                                    value={marketingEmail}
                                    onChange={(e) => {
                                      setMarketingEmail(e.target.value);
                                      setMarketingSaved(false);
                                      setMarketingClosing(false);
                                      setMarketingHidden(false);
                                    }}
                                    placeholder={tr("Email address")}
                                    maxLength={255}
                                    inputMode="email"
                                    autoComplete="email"
                                  />
                                </div>

                                {!!marketingErr && <div className="luxFbErr">{marketingErr}</div>}
                                {marketingSaved && (
                                  <div className="luxSuccessText">{tr("Your email has been saved.")}</div>
                                )}

                                <button
                                  className={`luxSendBtn ${marketingSending ? "isSending" : ""} ${(!isMarketingReady && !marketingSaved) ? "isDisabled" : ""}`}
                                  onClick={saveMarketingEmail}
                                  disabled={marketingSending || marketingSaved || !isMarketingReady}
                                >
                                  {marketingSending
                                    ? tr("Saving...")
                                    : marketingSaved
                                      ? tr("Saved")
                                      : tr("Save email")}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                        
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {uiPhase === "buzzer" && (
              <div className="luxActionRow">
                <div className={`luxBuzzerRow ${preFlipBurst ? "isBurst" : ""}`}>
                  <WaveIcon side="left" active={isRinging} size={24} />
            
                  <button
                    onClick={handleBuzzerClick}
                    disabled={isCooldown}
                    className={`luxButton ${isCooldown ? "isCooldown" : ""} ${isPressed ? "isPressed" : ""} ${
                      isRinging ? "isRinging" : ""
                    }`}
                  >
                    <div className="luxBtnBackplate" aria-hidden="true" />
                    <div className="luxBtnSheen" aria-hidden="true" />
                    <div className="luxBtnGlow" aria-hidden="true" />
            
                    <div className={`luxFlip ${showTimerFace ? "isFlipped" : ""}`}>
                      <div className="luxFlipInner">
                        <div className="luxFace luxFaceFront">
                          <span className={`luxBuzzer ${isRinging ? "isRinging" : ""}`}>
                            <span className="luxMetal" aria-hidden="true" />
                            <span className="luxBuzzerIcon">
                              <BuzzerIcon size={64} />
                            </span>
                            <span className="luxMicroSheen" aria-hidden="true" />
                          </span>
                        </div>
            
                        <div className="luxFace luxFaceBack">
                          <div className="luxTimerFace">
                            <SyncProgressRing progress={progress} label={""} className="luxRingBezel" variant="bezel" />
                            <div className="luxTimerHero" aria-label={`${remainingSec}`}>
                              {remainingSec}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
            
                  <WaveIcon side="right" active={isRinging} size={24} />
                </div>
            
                <div className="luxHint">
                  <span className={`luxHintDot ${showTimerFace ? "isCalled" : ""}`} aria-hidden="true" />
                  {showTimerFace ? tr("Staff Called! Please wait...") : tr("Tap to call staff")}
                </div>
                
                <div className="luxMsgRow">
                  <div className={`luxMsgShell ${isCooldown ? "isDisabled" : ""}`}>
                    <input
                      className="luxMsgInput"
                      value={msgText}
                      onChange={(e) => setMsgText(e.target.value)}
                      onBlur={() => {
                        // when user taps "OK/Done" on mobile keyboard, force restore
                        closeKeyboardAndRestoreView();
                      }}
                      placeholder={tr("..or tell us what you want here!")}
                      maxLength={255}
                      disabled={isCooldown || msgSending}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          sendBuzzerMessage();
                        }
                      }}
                    />
                
                    <button
                      type="button"
                      className={`luxMsgBtn ${msgSent ? "isSent" : ""}`}
                      disabled={isCooldown || msgSending || !String(msgText || "").trim()}
                      onClick={sendBuzzerMessage}
                      aria-label="Send message"
                      title="Send"
                    >
                      {msgSent ? "✓" : "➤"}
                    </button>
                  </div>
                
                  {!!msgErr && <div className="luxMsgErr">{msgErr}</div>}
                </div>                
                
              </div>
            )}
          </>   
        )}

        {!err && !!data && identityOpen && (
          <div
            className="luxModalOverlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeIdentityModal();
            }}
          >
            <div className="luxModalCard" role="dialog" aria-modal="true" aria-labelledby="luxIdentityTitle">
              <button
                type="button"
                className="luxModalClose"
                onClick={closeIdentityModal}
                aria-label={tr("Close")}
              >
                ×
              </button>

              <div className="luxModalEyebrow">{tr("Walk-in details")}</div>
              <div id="luxIdentityTitle" className="luxModalTitle">{tr("Save your contact details")}</div>
              <div className="luxModalSub">
                {tr("Add your details for a smoother and more personalized experience.")}
              </div>

              <div className="luxModalMeta">
                {!!arrivalDateText && (
                  <span className="luxModalMetaChip">
                    <span className="luxModalMetaIcon"><CalendarIcon /></span>
                    <span>{arrivalDateText}</span>
                  </span>
                )}
                {!!arrivalTimeText && (
                  <span className="luxModalMetaChip">
                    <span className="luxModalMetaIcon"><ClockIcon /></span>
                    <span>{arrivalTimeText}</span>
                  </span>
                )}
                {!!tables && (
                  <span className="luxModalMetaChip">
                    <span className="luxModalMetaIcon"><TableIcon /></span>
                    <span>{tables}</span>
                  </span>
                )}
              </div>

              <div className="luxFormGrid">
                <label className="luxField isRequired">
                  <span className="luxFieldLabel">{tr("Phone")}</span>
                  <input
                    className="luxFieldInput"
                    type="tel"
                    value={identityPhone}
                    onChange={(e) => setIdentityPhone(e.target.value)}
                    placeholder={tr("Mobile phone")}
                    autoComplete="tel"
                    inputMode="tel"
                    maxLength={15}
                  />
                </label>

                <label className="luxField">
                  <span className="luxFieldLabel">{tr("Last name")}</span>
                  <input
                    className="luxFieldInput"
                    type="text"
                    value={identityLastname}
                    onChange={(e) => setIdentityLastname(e.target.value)}
                    placeholder={tr("Last name")}
                    autoComplete="family-name"
                    maxLength={80}
                  />
                </label>

                <label className="luxField">
                  <span className="luxFieldLabel">{tr("First name")}</span>
                  <input
                    className="luxFieldInput"
                    type="text"
                    value={identityFirstname}
                    onChange={(e) => setIdentityFirstname(e.target.value)}
                    placeholder={tr("First name")}
                    autoComplete="given-name"
                    maxLength={80}
                  />
                </label>
              </div>

              {!!identityErr && <div className="luxFormErr">{identityErr}</div>}
              {identitySaved && (
                <div className="luxSavedFlash" role="status" aria-live="polite">
                  <span className="luxSavedFlashIcon">✓</span>
                  <span>{tr("Your details have been saved.")}</span>
                </div>
              )}

              <button
                className={`luxSendBtn ${identitySaving ? "isSending" : ""} ${!phoneReady ? "isDisabled" : ""} ${identitySaved ? "isSuccess" : ""}`}
                onClick={saveIdentity}
                disabled={identitySaving || identitySaved || !phoneReady}
              >
                {identitySaving ? tr("Saving...") : identitySaved ? tr("Saved") : tr("Save")}
              </button>
            </div>
          </div>
        )}

        {!err && !!data && termsOpen && (
          <div
            className="luxModalOverlay luxModalOverlayTerms"
            onClick={(e) => {
              if (e.target === e.currentTarget) setTermsOpen(false);
            }}
          >
            <div className="luxModalCard luxTermsCard" role="dialog" aria-modal="true" aria-labelledby="luxTermsTitle">
              <button
                type="button"
                className="luxModalClose"
                onClick={() => setTermsOpen(false)}
                aria-label={tr("Close")}
              >
                ×
              </button>

              <div className="luxModalEyebrow">{tr("Terms & conditions")}</div>
              <div id="luxTermsTitle" className="luxModalTitle">{tr("Email news & offers")}</div>
              <div className="luxTermsTextWrap">
                <p className="luxTermsText">{tr("By sharing your email address, you agree to receive occasional restaurant news, updates, invitations and promotional offers.")}</p>
                <p className="luxTermsText">{tr("Your email will be used only by the restaurant for its own communication and will not be sold to third parties.")}</p>
                <p className="luxTermsText">{tr("You can unsubscribe at any time by following the instructions included in a message or by contacting the restaurant directly.")}</p>
                <p className="luxTermsText">{tr("The restaurant remains responsible for the content, frequency and lawful processing of these communications.")}</p>
              </div>
            </div>
          </div>
        )}

        <MarketingBottomBar />
      </div>

    </div>
  );
}
